<template>
  <div class="gift-card-balance-root">
    <GlobalNavigation />

    <main class="py-8 mb-16">

      <BaseContainer>
        <div class="text-center">
          <BaseButton color="muted" to="/">
            <DropdownIcon class="rotate-90 h-4 w-4 mr-2 fill-text" />
            {{ $t('common.back_to_overview') }}
          </BaseButton>
    
          <h1 class="mb-6 mt-6 h1">{{ $t('check_giftcard.title') }}</h1>
        </div>
      </BaseContainer>

      <Form @submit="handleSubmit" @invalid-submit="onInvalidSubmit">
        <BaseContainer>
          <div class="max-w-[450px] mx-auto">
  
            <p>{{ $t('check_giftcard.description') }}</p>

            <div class="mb-4 mt-4">
              <InputText
                id="code"
                name="code"
                type="text"
                :rules="rules.code"
                v-model="form.code"
                :placeholder="$t('check_giftcard.code_placeholder')"
                class="mt-1 block w-full border-gray-300 rounded-md"
                required
              />
            </div>

            <BaseButton
              class="w-full relative group"
              color="primary"
              size="lg"
              type="submit"
              :disabled="isSubmitting"
            >
              <span class="w-full flex items-center justify-center">
                {{ $t('check_giftcard.submit') }}
                <ArrowRightIcon
                  class="w-4 h-4 ml-2 fill-current group-hover:animate-pulse-right"
                />
              </span>
            </BaseButton>

            <div v-if="errorMessage" class="text-sm block pt-2 text-error">
              {{ errorMessage }}
            </div>
          </div>
        </BaseContainer>
      </Form>

      <!-- Results Section -->
      <BaseContainer v-if="giftcardResult">
        <div class="max-w-[450px] mx-auto mt-8">
          <div class="bg-surface-grey-light rounded-md p-6 space-y-3">
            <div class="flex justify-between">
              <span class="font-semibold">{{ $t('check_giftcard.result_code') }}</span>
              <span>{{ giftcardResult.code }}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">{{ $t('check_giftcard.result_issue_date') }}</span>
              <span>{{ giftcardResult.issue_ts }}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">{{ $t('check_giftcard.result_expire_date') }}</span>
              <span>{{ giftcardResult.expire_ts }}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">{{ $t('check_giftcard.result_balance') }}</span>
              <span>{{ giftcardResult.currency }} {{ giftcardResult.balance }}</span>
            </div>
          </div>

          <!-- Transaction History -->
          <div v-if="giftcardResult.transactions.length > 0" class="mt-6">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b text-left">
                  <th class="pb-2 font-semibold">{{ $t('check_giftcard.transaction_header_date') }}</th>
                  <th class="pb-2 font-semibold">{{ $t('check_giftcard.transaction_header_type') }}</th>
                  <th class="pb-2 font-semibold text-right">{{ $t('check_giftcard.transaction_header_amount') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(transaction, index) in giftcardResult.transactions" :key="index" class="border-b last:border-b-0">
                  <td class="py-2">{{ transaction.ts }}</td>
                  <td class="py-2">{{ getTransactionTypeLabel(transaction.type) }}</td>
                  <td class="py-2 text-right">{{ transaction.amount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </BaseContainer>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { Form, type InvalidSubmissionHandler, type SubmissionHandler } from 'vee-validate';
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { checkGiftcard, type CheckGiftcardResult } from '@/api/checkGiftcard';
import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
import DropdownIcon from '@/components/icons/DropdownIcon.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseContainer from '@/components/ui/BaseContainer.vue';
import InputText from '@/components/ui/inputs/InputText.vue';

const { t } = useI18n();

useHead({
  title: t('check_giftcard.head_title'),
  meta: [
    { name: 'description', content: t('check_giftcard.description') },
    { property: 'og:title', content: t('check_giftcard.head_title') },
    { property: 'og:description', content: t('check_giftcard.description') },
  ],
});

const form = reactive({ code: '' });

const rules = {
  code: yup
    .string()
    .required(t('validation.required')),
};

const errorMessage = ref<string | null>(null);
const isSubmitting = ref(false);

type GiftcardSuccess = Extract<CheckGiftcardResult, { status: 'ok' }>;
const giftcardResult = ref<GiftcardSuccess | null>(null);

function getTransactionTypeLabel(type: string): string {
  const key = `check_giftcard.transaction_type_${type}`;
  const translated = t(key);
  // If no translation found, vue-i18n returns the key itself
  return translated === key ? type : translated;
}

const handleSubmit: SubmissionHandler = async (values) => {
  const { code } = values as { code: string };

  isSubmitting.value = true;
  errorMessage.value = null;
  giftcardResult.value = null;

  try {
    const result = await checkGiftcard(code);

    if (result.status === 'ok') {
      giftcardResult.value = result;
    } else if (result.error === 'check_giftcard_not_found') {
      errorMessage.value = t('check_giftcard.error_not_found');
    } else {
      errorMessage.value = t('check_giftcard.error_generic');
    }
  } catch {
    errorMessage.value = t('check_giftcard.error_generic');
  } finally {
    isSubmitting.value = false;
  }
};

const onInvalidSubmit: InvalidSubmissionHandler = () => {
  // vee-validate handles field-level errors
};
</script>

<style>
.min-h-screen:has( .gift-card-balance-root) {
  min-height: calc(100vh - 538px) !important;
}
</style>
